/** 图文热点标题 → 页内锚点 id（门户渲染 / 跳转共用） */

import { getHeaderOffset } from '@/components/shared/utils/viewportShell'

const DEFAULT_BACK_BAR_HEIGHT = 64
const ANCHOR_GAP = 16

/**
 * 将热点标题转为 URL hash 用的 slug。
 * 保留字母数字与 -_.，其余转为 -，并合并首尾多余的 -。
 */
export function slugifyHotspotHeading(heading) {
  const text = String(heading || '').trim()
  if (!text) return ''
  const slug = text
    .replace(/[^\p{L}\p{N}\-_.]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return slug || 'hotspot'
}

/**
 * 在 usedIds 集合中分配唯一锚点 id（同页冲突时追加 -2、-3…）。
 * @param {string} heading
 * @param {Set<string>} usedIds
 */
export function allocateHotspotAnchorId(heading, usedIds) {
  const base = slugifyHotspotHeading(heading)
  if (!base) return ''
  const used = usedIds instanceof Set ? usedIds : new Set()
  let id = base
  let n = 2
  while (used.has(id)) {
    id = `${base}-${n}`
    n += 1
  }
  used.add(id)
  return id
}

function resolveAnchorTarget(id, root) {
  const raw = String(id || '').trim()
  if (!raw) return null
  try {
    if (root?.querySelector) {
      const scoped = root.querySelector(`#${CSS.escape(raw)}`)
      if (scoped) return scoped
    }
  } catch {
    // ignore invalid selector
  }
  return typeof document !== 'undefined' ? document.getElementById(raw) : null
}

function resolveBackBarHeight() {
  if (typeof document === 'undefined') return DEFAULT_BACK_BAR_HEIGHT
  const bar = document.querySelector('.portal-back-bar')
  if (!bar) return DEFAULT_BACK_BAR_HEIGHT
  const height = Math.ceil(bar.getBoundingClientRect().height)
  return height > 0 ? height : DEFAULT_BACK_BAR_HEIGHT
}

/** 顶栏 + 吸顶返回条 + 间距，保证标题停在遮挡层下方 */
export function getHotspotAnchorScrollOffset() {
  return getHeaderOffset() + resolveBackBarHeight() + ANCHOR_GAP
}

/**
 * 滚动到指定锚点元素（避开固定顶栏与返回条）。
 * @param {Element} target
 * @param {{ behavior?: ScrollBehavior }} [options]
 */
export function scrollToHotspotTarget(target, { behavior = 'smooth' } = {}) {
  if (!target || typeof window === 'undefined') return false
  const offset = getHotspotAnchorScrollOffset()
  const top = Math.max(
    0,
    window.scrollY + target.getBoundingClientRect().top - offset,
  )
  window.scrollTo({ top, left: 0, behavior })
  return true
}

function decodeHashId(rawHash) {
  const raw = String(rawHash || '').replace(/^#/, '')
  try {
    return decodeURIComponent(raw).trim()
  } catch {
    return raw.trim()
  }
}

/** 滚动到当前 location.hash 对应的热点标题 */
export function scrollToHotspotHash(root, { behavior = 'auto' } = {}) {
  if (typeof location === 'undefined') return false
  const id = decodeHashId(location.hash)
  if (!id) return false
  const target = resolveAnchorTarget(id, root)
  if (!target) return false
  return scrollToHotspotTarget(target, { behavior })
}

/**
 * 绑定正文内 a[href^="#"] 点击，滚动到同页热点标题。
 * @param {HTMLElement} root
 */
export function bindHotspotAnchorLinks(root) {
  if (!root || root.__hotspotAnchorBound) return
  root.__hotspotAnchorBound = true
  root.addEventListener('click', (event) => {
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return
    const link = event.target?.closest?.('a[href^="#"]')
    if (!link || !root.contains(link)) return
    const href = link.getAttribute('href') || ''
    if (!href.startsWith('#') || href === '#') return
    const id = decodeHashId(href)
    if (!id) return
    const target = resolveAnchorTarget(id, root)
    if (!target) return
    event.preventDefault()
    try {
      const hash = /^[\w.\-]+$/u.test(id) ? `#${id}` : `#${encodeURIComponent(id)}`
      history.pushState(null, '', hash)
    } catch {
      // ignore history errors
    }
    scrollToHotspotTarget(target, { behavior: 'smooth' })
  })
}
